import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck, House } from "lucide-react";
import { usePrescriptionExitToStart } from "../context";
import { usePlatform } from "../../../../../platform/context";
import { useTimedTerminal } from "../../../use-timed-terminal";
import { TimedTerminalFooter } from "../../../components/TimedTerminalFooter";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useStepPrimaryAction } from "../../../context";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { useAudio } from "../../../../../lib/use-audio";

const AUTO_REDIRECT_SECONDS = 60;

/**
 * Request confirmation step — the one success screen of the prescription flow
 * (the former second "Vielen Dank" screen is merged into it, KO-82). It uses
 * the shared success tone (KO-13), a content-sized panel (KO-14), copy that
 * is specific to a prescription request without repeating itself (KO-19,
 * KO-81), the shared countdown with "Mehr Zeit" (KO-18), and "Zurück zum
 * Start" in the footer, which leaves the flow.
 *
 * Reached only after the request was actually accepted: a failed submission
 * returns to the review step, which announces the failure and offers a real
 * resubmit.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <RequestConfirmationStep />
 * ```
 */
export function RequestConfirmationStep() {
  const { t } = useTranslation();
  const exitToStart = usePrescriptionExitToStart();
  const platform = usePlatform();

  useAudio("prescription-requestConfirmationSuccess");

  // No default: an estimate is a promise about when the practice will be
  // finished, so it is shown only when the practice actually configured one.
  const [estimatedTime, setEstimatedTime] = useState<string | undefined>(
    undefined,
  );

  // Load estimated turnaround time from practice settings
  useEffect(
    function syncEstimatedTime() {
      void (async () => {
        try {
          const settings = await platform.getPracticeSettings();
          if (settings.prescriptionRequestEstimatedTime) {
            setEstimatedTime(settings.prescriptionRequestEstimatedTime);
          }
        } catch (error) {
          void reportOperationalError({
            action: "prescription.confirmation-settings-load",
            severity: "warning",
            source: "native",
            component: "RequestConfirmationStep",
            error,
          });
        }
      })();
    },
    [platform],
  );

  const { countdown, hasExtended, extendTime } = useTimedTerminal({
    initialSeconds: AUTO_REDIRECT_SECONDS,
    onExpire: exitToStart,
    useReassuranceOverlay: true,
  });

  useStepPrimaryAction({
    label: t("self-service.prescription.confirmation.back-to-start"),
    icon: House,
    tone: "success",
    testId: "frontdesk-prescription-confirmation-done-btn",
    onClick: exitToStart,
  });

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.prescription.confirmation.success-title")}
    >
      <KioskStatePanel
        tone="success"
        testId="frontdesk-prescription-confirmation-panel"
        icon={
          <CircleCheck data-testid="frontdesk-prescription-confirmation-success-icon" />
        }
        title={t("self-service.prescription.confirmation.success-title")}
        body={t("self-service.prescription.confirmation.next-steps")}
      >
        {estimatedTime === undefined ? null : (
          <p
            data-testid="frontdesk-prescription-confirmation-turnaround"
            className="m-0 text-xl font-semibold text-foreground"
          >
            {t("self-service.prescription.confirmation.estimated-time", {
              time: estimatedTime,
            })}
          </p>
        )}
        <TimedTerminalFooter
          countdown={countdown}
          hasExtended={hasExtended}
          onExtendTime={extendTime}
        />
      </KioskStatePanel>
    </KioskStep>
  );
}
